import type { HTMLAttributes } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL_CLASS } from '@/styles/fieldControl';
import { FormFieldLabel } from './FormFieldLabel';
import { FULL_ROW_FIELD_CLASS, LEADING_SYMBOL_CLASS } from './formDialog.constants';

interface FormTextFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
  placeholder?: string | undefined;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'] | undefined;
  leadingSymbol?: string | undefined;
  hasTabularNumbers?: boolean | undefined;
  isUpperCase?: boolean | undefined;
  isRequired?: boolean | undefined;
  maxLength?: number | undefined;
  hasCharacterCount?: boolean | undefined;
  isFullRow?: boolean | undefined;
}

export function FormTextField<TValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  inputMode,
  leadingSymbol,
  hasTabularNumbers = false,
  isUpperCase = false,
  isRequired = false,
  maxLength,
  hasCharacterCount = false,
  isFullRow = false,
}: FormTextFieldProps<TValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(isFullRow && FULL_ROW_FIELD_CLASS)}>
          <FormFieldLabel
            label={label}
            isRequired={isRequired}
            countedValue={field.value}
            characterLimit={hasCharacterCount ? maxLength : undefined}
          />
          <div className="relative">
            {leadingSymbol === undefined ? null : (
              <span className={LEADING_SYMBOL_CLASS} aria-hidden>
                {leadingSymbol}
              </span>
            )}
            <FormControl>
              <Input
                {...field}
                onChange={(event) =>
                  field.onChange(
                    isUpperCase ? event.target.value.toUpperCase() : event.target.value,
                  )
                }
                placeholder={placeholder}
                maxLength={maxLength}
                aria-required={isRequired || undefined}
                inputMode={inputMode}
                autoComplete="off"
                className={cn(
                  FIELD_CONTROL_CLASS,
                  leadingSymbol !== undefined && 'pl-8',
                  hasTabularNumbers && 'tabular-nums',
                  isUpperCase && 'uppercase',
                )}
              />
            </FormControl>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
